/**
 * TK-38 việc 2f (lee 23/10) — BẢNG ĐÈN CỦA TUYẾN ĐANG CHỌN (dưới khung tuyến ở trang Vận hành).
 *   Cột: Tên · Tủ · Nhánh · % · P (W) · U (V) · I (mA) · PF · Mạng · Cập nhật. Lọc theo tủ, bấm tiêu đề để sắp, ≥ 200 dòng ⇒ phân
 *   trang 100 dòng. Bấm một dòng ⇒ `onChonDen(khoá)` (chấm đèn sáng lên trên bản đồ). Luật số 0: NULL ⇒ "—".
 * KA-49 §4.4 (mẫu `mau_van_hanh`): thêm Nhóm · **Muốn** (lệnh gần nhất) · **Báo** (`dim` đèn tự báo, thay cột "%") · Trạng thái
 *   (Đạt / Đang chờ / Không trả lời / Sai mức — `lib/tienTrinhLenh.ts`). Dòng Muốn ≠ Báo tô vàng; dòng đang chọn nổi lên.
 *   "Cập nhật" đổi tên "Tuổi số". Muốn chưa tải ⇒ "—" xám (không coi là "không có lệnh").
 */
import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MAU_BADGE, NHAN_BADGE, tenDen } from '../../lib/nemaNhan';
import { tuoiNgan } from '../../lib/nemaApi';
import { iMa, lucCapNhat, pfHai } from '../../lib/vanHanh';
import { NHAN_KET_QUA, type KetQuaDen } from '../../lib/tienTrinhLenh';
import type { DenBanDo } from '../nema/LopDenNema';

type Cot = 'ten' | 'tu' | 'nhanh' | 'nhom' | 'muon' | 'dim' | 'p' | 'u' | 'i' | 'pf' | 'mang' | 'luc' | 'tt';
const COT: { k: Cot; ten: string; so?: boolean }[] = [
  { k: 'ten', ten: 'Tên' }, { k: 'tu', ten: 'Tủ' }, { k: 'nhanh', ten: 'Nhánh' }, { k: 'nhom', ten: 'Nhóm', so: true },
  { k: 'muon', ten: 'Muốn', so: true }, { k: 'dim', ten: 'Báo', so: true },
  { k: 'p', ten: 'P (W)', so: true }, { k: 'u', ten: 'U (V)', so: true }, { k: 'i', ten: 'I (mA)', so: true }, { k: 'pf', ten: 'PF', so: true },
  { k: 'mang', ten: 'Mạng' }, { k: 'luc', ten: 'Tuổi số' }, { k: 'tt', ten: 'Trạng thái' },
];
/** KA-49: Muốn của một đèn — undefined = chưa tải · null = không có lệnh trong 6 giờ · `ket` null = về lịch (không chấm). */
export type MuonDen = { muon: string; muc: number | null; ket: KetQuaDen | null } | null | undefined;
const TRANG = 100;
const NGUONG_PHAN_TRANG = 200;
const giaTri = (d: DenBanDo, k: Cot, mu: MuonDen): string | number | null => {
  switch (k) {
    case 'ten': return tenDen(d); case 'tu': return d.tenTu; case 'nhanh': return d.nhanh; case 'dim': return d.dim;
    case 'nhom': return d.nhomTb ?? null; case 'muon': return mu?.muc ?? null; case 'tt': return mu?.ket ? NHAN_KET_QUA[mu.ket].nhan : null;
    case 'p': return d.p_w; case 'u': return d.u_v ?? null; case 'i': return iMa(d.i_10ma); case 'pf': return d.pf_pct ?? null;
    case 'mang': return d.online == null ? null : d.online ? 1 : 0; case 'luc': return lucCapNhat(d);
  }
};
const so = (v: number | null | undefined) => (v == null ? '—' : String(v));
export const khoaDen = (d: { cabinetId: string; addr: number }) => `${d.cabinetId}:${d.addr}`;
const khongMuon = (): MuonDen => undefined;

export function BangDenTuyen({ den, noiBat, onChonDen, moDong = null, chiTiet, className = '', tuChuaCai = [], lienKetCai = false, muon = khongMuon }: {
  den: DenBanDo[]; noiBat: string | null; onChonDen: (k: string) => void;
  /** KA-49: cột Muốn / Trạng thái (lệnh gần nhất của đèn) */
  muon?: (d: DenBanDo) => MuonDen;
  /** TK-44: tủ của tuyến CHƯA có bảng đèn đã lưu — mỗi tủ một dòng "SIM-03 · Chưa cài đèn" (+ link Cài đặt ban đầu nếu có quyền) */
  tuChuaCai?: { id: string; name: string }[]; lienKetCai?: boolean;
  /** Vận hành 24/10: dòng đang mở ô thông số ngay bên dưới (khoá đèn) + nội dung ô */
  moDong?: string | null; chiTiet?: (d: DenBanDo) => ReactNode; className?: string;
}) {
  const [tu, setTu] = useState('');
  const [sap, setSap] = useState<{ k: Cot; tang: boolean }>({ k: 'ten', tang: true });
  const [trang, setTrang] = useState(0);
  const dsTu = useMemo(() => [...new Map(den.map((d) => [d.cabinetId, d.tenTu])).entries()], [den]);
  const ds = useMemo(() => {
    const loc = tu ? den.filter((d) => d.cabinetId === tu) : den;
    const k = sap.k;
    return [...loc].sort((a, b) => {
      const x = giaTri(a, k, muon(a)), y = giaTri(b, k, muon(b));
      if (x === y) return 0; if (x === null) return 1; if (y === null) return -1;   // "—" luôn xuống cuối
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'vi', { numeric: true });
      return sap.tang ? c : -c;
    });
  }, [den, tu, sap, muon]);
  const phanTrang = ds.length >= NGUONG_PHAN_TRANG;
  const soTrang = phanTrang ? Math.ceil(ds.length / TRANG) : 1;
  const tr = Math.min(trang, soTrang - 1);
  const hien = phanTrang ? ds.slice(tr * TRANG, tr * TRANG + TRANG) : ds;
  return (
    <section className={`khoi min-w-0 overflow-hidden rounded-the border border-vien bg-nen-1 ${className}`} data-o="bang-den-tuyen" aria-label="Đèn của tuyến">
      <div className="flex flex-wrap items-center gap-2 border-b border-vien px-3 py-2">
        <b className="text-sm text-chu-1">Đèn của tuyến · {ds.length}</b>
        <span className="hidden text-xs text-chu-2 md:inline">"Muốn" là lệnh gần nhất, "Báo" là đèn tự báo về</span>
        <select value={tu} onChange={(e) => { setTu(e.target.value); setTrang(0); }} aria-label="Lọc theo tủ"
          className="ml-auto rounded-nut border border-vien bg-nen-lom px-2 py-1.5 text-sm text-chu-1">
          <option value="">Mọi tủ</option>
          {dsTu.map(([id, ten]) => <option key={id} value={id}>{ten}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] border-collapse text-sm">
          <thead>
            <tr className="op-dau-bang text-left text-xs text-chu-2">
              {COT.map((c) => (
                <th key={c.k} className={`whitespace-nowrap px-2.5 py-2 font-medium ${c.so ? 'text-right' : ''}`}>
                  <button type="button" className="hover:text-chu-1" onClick={() => setSap((s) => ({ k: c.k, tang: s.k === c.k ? !s.tang : true }))} aria-sort={sap.k === c.k ? (sap.tang ? 'ascending' : 'descending') : undefined}>
                    {c.ten}{sap.k === c.k ? (sap.tang ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hien.map((d) => {
              const k = khoaDen(d);
              const mu = muon(d);
              /* Muốn ≠ Báo (cả hai có số) ⇒ dòng lệch tô vàng */
              const lech = mu?.muc != null && d.dim != null && mu.muc !== d.dim;
              return (
                <Fragment key={k}>
                <tr onClick={() => onChonDen(k)} data-dong-den={k} aria-selected={noiBat === k} data-lech={lech ? '1' : undefined}
                  className={`op-dong-den cursor-pointer border-t border-vien hover:bg-nen-2 ${noiBat === k ? 'bg-nen-2' : ''}`}>
                  <td className="max-w-[180px] truncate px-2.5 py-2 font-medium text-chu-1">
                    <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-[-1px]" style={{ background: MAU_BADGE[d.badge] }} title={NHAN_BADGE[d.badge].nhan} aria-label={NHAN_BADGE[d.badge].nhan} data-badge={d.badge} />{tenDen(d)}
                  </td>
                  <td className="max-w-[160px] truncate px-2.5 py-2 text-chu-2">{d.tenTu}</td>
                  <td className="px-2.5 py-2 text-chu-2">{d.nhanh == null ? '—' : `Ngõ ${d.nhanh}`}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-chu-2">{so(d.nhomTb)}</td>
                  <td className={`px-2.5 py-2 text-right tabular-nums ${mu ? '' : 'text-chu-3'}`} data-o="muon">{mu ? mu.muon : '—'}</td>
                  <td className={`px-2.5 py-2 text-right tabular-nums ${lech ? 'font-semibold text-canh' : ''}`} data-o="bao">{d.dim == null ? '—' : `${d.dim} %`}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{so(d.p_w)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{so(d.u_v)}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{so(iMa(d.i_10ma))}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums">{pfHai(d.pf_pct) ?? '—'}</td>
                  <td className={`px-2.5 py-2 ${d.online ? 'text-chu-1' : 'text-chu-3'}`}>{d.online == null ? '—' : d.online ? 'Trong mạng' : 'Mất mạng'}</td>
                  <td className="px-2.5 py-2 text-chu-2">{tuoiNgan(lucCapNhat(d))}</td>
                  <td className="px-2.5 py-2" data-o="trang-thai">{mu?.ket
                    ? <span className={`op-tt-nhan op-tt-${NHAN_KET_QUA[mu.ket].tone}`} data-ket={mu.ket}>{NHAN_KET_QUA[mu.ket].nhan}</span>
                    : <span className="text-chu-3">{mu && mu.muc === null ? 'Theo lịch' : '—'}</span>}</td>
                </tr>
                {moDong === k && chiTiet && (
                  <tr className="bg-nen-0/40" data-dong-chi-tiet={k}>
                    <td colSpan={COT.length} className="px-2.5 pb-3 pt-1">
                      <div className="sticky left-2.5 max-w-[min(520px,calc(100vw-48px))]">{chiTiet(d)}</div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {(tu === '' ? tuChuaCai : tuChuaCai.filter((t) => t.id === tu)).map((t) => (
              <tr key={`chua-cai-${t.id}`} className="border-t border-vien" data-tu-chua-cai={t.id}>
                <td colSpan={COT.length} className="px-2.5 py-2.5 text-chu-3">
                  <b className="font-medium text-chu-2">{t.name}</b> · Chưa cài đèn
                  {lienKetCai && <Link to={`/cai-dat-ban-dau?tu=${t.id}`} className="ml-3 text-nhan hover:underline">Cài đặt ban đầu</Link>}
                </td>
              </tr>
            ))}
            {hien.length === 0 && tuChuaCai.length === 0 && <tr><td colSpan={COT.length} className="px-3 py-5 text-center text-chu-3">Tuyến chưa có đèn</td></tr>}
          </tbody>
        </table>
      </div>
      {phanTrang && (
        <div className="flex items-center justify-end gap-2 border-t border-vien px-3 py-2 text-sm text-chu-2" data-o="phan-trang">
          <button type="button" disabled={tr === 0} onClick={() => setTrang(tr - 1)} className="rounded-nut border border-vien px-2 py-1 disabled:opacity-40">‹</button>
          <span className="tabular-nums">{tr + 1}/{soTrang}</span>
          <button type="button" disabled={tr >= soTrang - 1} onClick={() => setTrang(tr + 1)} className="rounded-nut border border-vien px-2 py-1 disabled:opacity-40">›</button>
        </div>
      )}
    </section>
  );
}
